import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Activity, Heart, Thermometer, Droplets, Weight, HeartPulse, AlertTriangle, Pencil, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { VitalSparkline } from './VitalSparkline';
import { EditVitalExecutionDialog, type EditVitalExecutionMode } from './EditVitalExecutionDialog';

interface VitalExecution {
  id: string;
  executed_at: string;
  vital_value: number | null;
  vital_value_secondary: number | null;
  is_alert: boolean;
  task_name: string;
  vital_type: string | null;
  unit: string | null;
  min_alert_value: number | null;
  max_alert_value: number | null;
  notes?: string | null;
  edited_at?: string | null;
}

interface VitalsSummaryTableProps {
  vitals: VitalExecution[];
  recentHistory?: Record<string, Array<{ value: number; isAlert?: boolean }>>;
  patientId?: string;
  canEdit?: boolean;
  onChanged?: () => void;
}

const VITAL_ICONS: Record<string, React.ElementType> = {
  blood_pressure: Heart,
  glucose: Droplets,
  temperature: Thermometer,
  oxygen_saturation: Activity,
  weight: Weight,
  heart_rate: HeartPulse,
};

export function VitalsSummaryTable({ vitals, recentHistory = {}, patientId, canEdit = false, onChanged }: VitalsSummaryTableProps) {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogState, setDialogState] = useState<EditVitalExecutionMode | null>(null);

  const alertCount = useMemo(() => vitals.filter(v => v.is_alert).length, [vitals]);

  if (vitals.length === 0 && !canEdit) return null;

  const openCreate = () => {
    setDialogState({ mode: 'create' });
    setDialogOpen(true);
  };
  const openEdit = (v: VitalExecution) => {
    setDialogState({
      mode: 'edit',
      executionId: v.id,
      vitalType: v.vital_type,
      initialValue: v.vital_value,
      initialSecondary: v.vital_value_secondary,
      initialNotes: v.notes ?? null,
      taskName: v.task_name,
    });
    setDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              {t('dailyReport.vitalsSummary')}
            </CardTitle>
            <div className="flex items-center gap-2">
              {alertCount > 0 && (
                <Badge variant="destructive" className="text-[11px]">
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  {alertCount} {alertCount === 1 ? t('dailyReport.alert') : t('dailyReport.alertsPlural')}
                </Badge>
              )}
              {canEdit && patientId && (
                <Button size="sm" variant="outline" className="h-7 px-2" onClick={openCreate}>
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Registrar
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {vitals.map(v => {
              const Icon = VITAL_ICONS[v.vital_type || ''] || Activity;
              const formatValue = () => {
                if (!v.vital_value) return '—';
                if (v.vital_type === 'blood_pressure' && v.vital_value_secondary) {
                  return `${v.vital_value} × ${v.vital_value_secondary}`;
                }
                return `${v.vital_value} ${v.unit || ''}`;
              };

              const history = recentHistory[v.vital_type || ''] || [];

              return (
                <div key={v.id} className="px-3 py-2.5 space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-full ${v.is_alert ? 'bg-destructive/10 text-destructive' : 'bg-primary/10 text-primary'}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <span className="text-sm font-medium">{v.task_name}</span>
                        <p className="text-[11px] text-muted-foreground">
                          {format(new Date(v.executed_at), 'HH:mm', { locale: ptBR })}
                          {v.edited_at && (
                            <span className="ml-1 italic">· editado</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="text-right">
                        <span className={`text-base font-bold ${v.is_alert ? 'text-destructive' : 'text-foreground'}`}>
                          {formatValue()}
                        </span>
                        {v.is_alert && <AlertTriangle className="w-3.5 h-3.5 text-destructive inline ml-1" />}
                      </div>
                      {canEdit && patientId && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => openEdit(v)}
                          aria-label="Editar medição"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {history.length >= 2 && v.vital_value && (
                    <VitalSparkline
                      readings={history}
                      min={v.min_alert_value}
                      max={v.max_alert_value}
                      unit={v.unit || ''}
                      currentValue={v.vital_value}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {patientId && (
        <EditVitalExecutionDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          patientId={patientId}
          state={dialogState}
          onSaved={onChanged}
        />
      )}
    </>
  );
}
